// Phase P.11 — Reusable status-history timeline for InvoiceDetail + BookingDetail.
//
// Renders a collapsible section. Each row: [icon] to_status • actor · time.
// Icon + tint color-code by event type (paid/sent/completed/etc).

import { useEffect, useState } from "react";
import {
  ChevronDown, ChevronUp, Clock, CheckCircle2, FileText,
  Send, Receipt, Ban, RotateCw, MailX, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatBookingStamp, formatEventTime } from "@/lib/dates";

const EVENT_META = {
  // to_status keys → icon + tailwind classes (border + bg + text tint).
  completed:               { icon: CheckCircle2, tint: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  paid:                    { icon: Receipt,      tint: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  issued:                  { icon: FileText,     tint: "text-sky-700 bg-sky-50 border-sky-200" },
  sent:                    { icon: Send,         tint: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  retry_send:              { icon: RotateCw,     tint: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  send_blocked:            { icon: MailX,        tint: "text-rose-700 bg-rose-50 border-rose-200" },
  refunded:                { icon: Ban,          tint: "text-amber-700 bg-amber-50 border-amber-200" },
  voided:                  { icon: Ban,          tint: "text-slate-600 bg-slate-50 border-slate-200" },
  draft:                   { icon: FileText,     tint: "text-slate-600 bg-slate-50 border-slate-200" },
  scheduled:               { icon: Clock,        tint: "text-slate-600 bg-slate-50 border-slate-200" },
  in_progress:             { icon: Sparkles,     tint: "text-amber-700 bg-amber-50 border-amber-200" },
  cancelled:               { icon: Ban,          tint: "text-rose-700 bg-rose-50 border-rose-200" },
  pending_manager_approval:{ icon: Clock,        tint: "text-amber-700 bg-amber-50 border-amber-200" },
};

function pickMeta(to_status) {
  return EVENT_META[to_status] || { icon: Clock, tint: "text-slate-600 bg-slate-50 border-slate-200" };
}

function labelForStatus(s) {
  if (!s) return "";
  return String(s).replace(/_/g, " ");
}

export default function StatusHistory({ entityType, entityId, defaultOpen = false, testIdPrefix = "history" }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!entityType || !entityId || !open) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    let url;
    if (entityType === "invoice") url = `/invoices/${entityId}/history`;
    else if (entityType === "cash_ledger_entry") url = `/manager-ledger/entries/${entityId}/history`;
    else if (entityType === "staff_ledger_entry") url = `/staff-ledger/${entityId}/history`;
    else if (entityType === "cash_holding") url = `/cash-holdings/${entityId}/history`;
    else url = `/bookings/${entityId}/history`;
    api
      .get(url)
      .then((r) => {
        if (cancelled) return;
        setRows(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Unable to load history");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [entityType, entityId, open]);

  return (
    <section className="border border-border rounded-[8px] bg-card" data-testid={`${testIdPrefix}-section`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold"
        data-testid={`${testIdPrefix}-toggle`}
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" /> History
          {rows.length > 0 && (
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {rows.length}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid={`${testIdPrefix}-panel`}>
          {loading && (
            <div className="text-sm text-muted-foreground py-3">Loading…</div>
          )}
          {err && !loading && (
            <div className="text-sm text-rose-700 py-3">{err}</div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="text-sm text-muted-foreground py-3" data-testid={`${testIdPrefix}-empty`}>
              No status history yet.
            </div>
          )}
          {!loading && !err && rows.length > 0 && (
            <ol className="relative border-l border-border ml-2 mt-2 space-y-3">
              {rows.map((ev) => {
                const meta = pickMeta(ev.to_status);
                const Icon = meta.icon;
                return (
                  <li
                    key={ev.id}
                    className="ml-3 relative"
                    data-testid={`${testIdPrefix}-row`}
                    data-event-to={ev.to_status}
                  >
                    <span className={`absolute -left-[19px] top-0 inline-flex h-5 w-5 items-center justify-center rounded-full border ${meta.tint}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                      <span className="font-semibold capitalize">{labelForStatus(ev.to_status)}</span>
                      {ev.from_status && (
                        <span className="text-xs text-muted-foreground">
                          from <span className="mono">{labelForStatus(ev.from_status)}</span>
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {ev.actor_name || "system"}
                        {ev.actor_role ? ` (${ev.actor_role})` : ""}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mono" title={ev.at}>
                      {formatBookingStamp(ev.at)} · {formatEventTime(ev.at)}
                    </div>
                    {ev.note && (
                      <div className="text-xs text-muted-foreground italic mt-1">{ev.note}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
